import React from 'react';
import { ShieldCheck, Brush, SmilePlus, Stethoscope, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

export function WarrantyCoverage() {
  const items = [
    { icon: <Brush size={28} />, title: "Fillings" },
    { icon: <SmilePlus size={28} />, title: "Crowns & Bridges" },
    { icon: <ShieldCheck size={28} />, title: "Implants" },
    { icon: <Stethoscope size={28} />, title: "Dentures & Partials" },
    { icon: <Sparkles size={28} />, title: "Veneers" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.6 }
    },
    hover: { 
      y: -8,
      transition: { duration: 0.3 }
    }
  };

  return (
    <section className="bg-white py-16 md:py-24 px-6 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ 
          backgroundImage: 'radial-gradient(circle, #0CB6B5 1px, transparent 1px)', 
          backgroundSize: '30px 30px' 
        }}></div>
      </div>

      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-block">
            <span className="inline-block bg-gradient-to-r from-teal-500 to-green-500 h-1 w-10 mb-2 mx-auto rounded"></span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 relative">
              What's Covered Under 
              <span className="relative">
                <span className="bg-gradient-to-r from-teal-500 to-green-500 text-transparent bg-clip-text"> Our Warranty</span>
                <svg className="absolute -bottom-2 left-2 w-32 h-3 text-green-200" viewBox="0 0 100 10" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0,5 Q25,0 50,5 T100,5" fill="none" stroke="currentColor" strokeWidth="2" />
                </svg>
              </span>
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Our comprehensive warranty covers a wide range of dental procedures to ensure your smile stays pristine.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 md:gap-10"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {items.map((item, i) => (
            <motion.div
              key={i}
              className="flex flex-col items-center space-y-4"
              variants={itemVariants}
              whileHover="hover"
            >
              <motion.div 
                className="p-5 bg-gradient-to-br from-teal-500 to-green-400 text-white rounded-xl shadow-lg"
                whileHover={{ rotate: [0, -5, 5, -5, 0], transition: { duration: 0.5 } }}
              >
                {item.icon}
              </motion.div>
              <h3 className="text-md font-semibold text-gray-800">{item.title}</h3>
              <div className="h-1 w-8 bg-gradient-to-r from-teal-400 to-green-300 rounded-full opacity-70"></div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <a href="#details" className="inline-flex items-center text-teal-600 font-medium hover:text-teal-700 transition-colors">
            View Complete Warranty Details
            <svg className="ml-2 w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </motion.div>
      </div>

      {/* Decorative elements */}
      <div className="hidden md:block absolute -bottom-12 -left-12 w-64 h-64 bg-gradient-to-tr from-teal-100 to-green-100 rounded-full opacity-40 blur-2xl"></div>
      <div className="hidden md:block absolute -top-24 -right-24 w-96 h-96 bg-gradient-to-bl from-teal-100 to-green-100 rounded-full opacity-30 blur-3xl"></div>
    </section>
  );
}

export default WarrantyCoverage;